/**
 * UserLogic - Service functions for User POJO operations
 */

import type { User } from './types';
import { trimOrUndefined } from '@/util/string';
import {
  validateUser,
  validateUserInAPIResponse as validateNestedUser,
} from './schema';

export const UserLogic = {
  /**
   * Create a User from JSON response.
   * Uses Zod schema: normalizes empty strings to undefined for optional fields; parses dates.
   * @throws Error if data is invalid
   */
  fromJSON(data: unknown): User {
    const user = validateUser(data);
    if (!user) {
      throw new Error('Invalid user data');
    }
    return user;
  },

  /**
   * Parse User from API response. Expects { user: User | null }.
   * Returns null for guest (when data.user is null/undefined).
   */
  fromNestedUserJSON(data: unknown): User | null {
    return validateNestedUser(data);
  },

  /** The user with its text identity fields (id, email) trimmed. */
  withTrimmedText(user: User): User {
    return {
      ...user,
      id: user.id.trim(),
      email: trimOrUndefined(user.email),
    };
  },
};
